// **Was das Dashboard zeigt, ohne es zu zeichnen** — rein, getestet
// (dashboard-model.test.ts). Die Komponenten setzen nur noch zusammen.

import type { AttentionItem, Dashboard, DashboardTenant, HostCapacity } from "../../api/types";
import { fmtAgo, fmtInt, fmtUsd, levelTone, monthCostUsd, pct } from "../../lib/format";
import type { Tone } from "../../lib/labels";

export const SEVERITY: Record<string, [string, Tone]> = {
  error: ["Fehler", "error"],
  warn: ["Warnung", "warn"],
  info: ["Hinweis", "info"],
};

export interface KpiTile {
  label: string;
  value: string;
  sub: string;
  bar: { pct: number; tone: Tone } | null;
  href: string;
}

/** „Oktober" aus `2026-10`; ein unlesbarer Monat bleibt, wie er ist. */
export function monthName(month: string): string {
  const d = new Date(`${month}-01T00:00:00`);
  return Number.isNaN(d.getTime()) ? month : d.toLocaleDateString("de-DE", { month: "long" });
}

const gb = (mb: number) => (mb / 1024).toFixed(1);

/** Die vier Kennzahlen oben: Mandanten, Benutzer, Kosten im Monat, Host. */
export function kpiTiles(d: Dashboard, hostPending = false): KpiTile[] {
  const t = d.totals;
  const tenantSub = [
    t.tenants.draft ? `${t.tenants.draft} ${t.tenants.draft === 1 ? "Entwurf" : "Entwürfe"}` : "",
    t.tenants.failed ? `${t.tenants.failed} mit Fehler` : "",
    t.tenants.suspended ? `${t.tenants.suspended} angehalten` : "",
  ].filter(Boolean).join(" · ") || "keine offenen Entwürfe";
  const flows = t.cost.flow_key_usd ?? 0;
  const cost = t.cost.platform_key_usd + flows + t.cost.instance_usd;
  const budgetPct = t.cost.budget_usd ? pct(t.cost.platform_key_usd, t.cost.budget_usd) : null;
  const h = d.host;
  let hostTile: KpiTile;
  if (h && h.memory_available_mb != null) {
    const used = h.memory_mb - h.memory_available_mb;
    const p = pct(used, h.memory_mb);
    hostTile = {
      label: "Arbeitsspeicher Host",
      value: `${Math.round(p)} %`,
      sub: `${gb(used)} von ${gb(h.memory_mb)} GB · ${fmtInt(h.containers_running)} Container`,
      bar: { pct: p, tone: levelTone(p, 80, 90) },
      href: "",
    };
  } else {
    hostTile = { label: "Host", value: "—", sub: hostPending ? "wird gemessen …" : "Provisioner nicht erreichbar", bar: null, href: "" };
  }
  return [
    { label: "Aktive Mandanten", value: fmtInt(t.tenants.active), sub: tenantSub, bar: null, href: "/tenants" },
    {
      label: "Benutzer",
      value: fmtInt(t.users.approved),
      sub: `${fmtInt(t.users.active_7d)} aktiv in 7 Tagen${t.users.pending ? ` · ${fmtInt(t.users.pending)} warten` : ""}`,
      bar: null,
      href: "",
    },
    {
      label: `Kosten ${monthName(d.month)}`,
      value: fmtUsd(cost),
      sub: `Plattform-Keys ${fmtUsd(t.cost.platform_key_usd)}${flows ? ` · Flows ${fmtUsd(flows)}` : ""} · eigene ${fmtUsd(t.cost.instance_usd)}`,
      bar: budgetPct === null ? null : { pct: budgetPct, tone: levelTone(budgetPct) },
      href: "/costs",
    },
    hostTile,
  ];
}

/**
 * „Braucht Aufmerksamkeit": Fehler und Warnungen immer; Hinweise nur, solange
 * sie unter den ersten acht Einträgen stehen — der Rest klappt zu. Die Liste
 * soll das Wichtigste zeigen, nicht die Seite füllen.
 */
export function splitAttention(items: AttentionItem[], show = 8): { shown: AttentionItem[]; more: AttentionItem[]; summary: string } {
  const counts = { error: 0, warn: 0, info: 0 };
  for (const i of items) counts[i.severity] += 1;
  const summary = [counts.error && `${counts.error} Fehler`, counts.warn && `${counts.warn} Warnungen`, counts.info && `${counts.info} Hinweise`]
    .filter(Boolean).join(" · ");
  const shown = items.filter((i, n) => i.severity !== "info" || n < show);
  const more = items.filter((i) => !shown.includes(i));
  return { shown, more, summary };
}

const totalCost = (t: DashboardTenant) => monthCostUsd(t.cost);

/** Die Mandanten nach Kosten im Monat, teuerste zuerst; bei Gleichstand nach Namen. */
export function sortTenantsByCost(tenants: DashboardTenant[]): DashboardTenant[] {
  return [...tenants].sort((a, b) => totalCost(b) - totalCost(a) || (a.name || a.slug).localeCompare(b.name || b.slug));
}

export function tenantTotal(t: DashboardTenant): number {
  return totalCost(t);
}

/** Der Hinweis unter dem Titel: wie alt der Stand der Instanzen und des Hosts ist. */
export function standText(d: Dashboard | null, { refreshing, fromCache, now = Date.now() }: { refreshing: boolean; fromCache: boolean; now?: number }): string {
  if (refreshing) return "Frage die Instanzen nach ihrem aktuellen Stand …";
  return [
    d?.refreshed_at ? `Stand der Instanzen: ${fmtAgo(d.refreshed_at, now)}` : "Noch kein Stand von den Instanzen",
    d?.host_measured_at ? `Host gemessen ${fmtAgo(d.host_measured_at, now)}` : "",
    fromCache ? "wird aktualisiert …" : "",
  ].filter(Boolean).join(" · ");
}

export interface Gauge {
  label: string;
  value: string;
  pct: number;
  tone: Tone;
}

/** Die Füllstände der Host-Karte: Speicher, Last, Platte — was der Host meldet. */
export function hostGauges(h: HostCapacity): Gauge[] {
  const out: Gauge[] = [];
  if (h.memory_available_mb != null) {
    const used = h.memory_mb - h.memory_available_mb;
    const p = pct(used, h.memory_mb);
    out.push({ label: "Arbeitsspeicher", value: `${gb(used)} / ${gb(h.memory_mb)} GB`, pct: p, tone: levelTone(p, 80, 90) });
  }
  if (h.load) {
    const p = pct(h.load[1], h.cpus);
    out.push({ label: "Last (5 min)", value: `${h.load[1].toFixed(2)} bei ${h.cpus} CPUs`, pct: p, tone: levelTone(p, 75, 100) });
  }
  if (h.disk) {
    const p = pct(h.disk.used_gb, h.disk.total_gb);
    out.push({ label: "Platte", value: `${Math.round(h.disk.used_gb)} / ${Math.round(h.disk.total_gb)} GB`, pct: p, tone: levelTone(p, 80, 90) });
  }
  return out;
}

export interface InstanceBar {
  key: string;
  name: string;
  pct: number;
  value: string;
  containers: number;
}

/** Speicher je Mandant (und Plattform), größter zuerst, relativ zum größten. */
export function instanceBars(h: HostCapacity, names: Map<string, string>): InstanceBar[] {
  const inst = Object.entries(h.instances ?? {}).sort((a, b) => b[1].memory_mb - a[1].memory_mb);
  const maxMem = Math.max(1, ...inst.map(([, v]) => v.memory_mb));
  return inst.map(([k, v]) => ({
    key: k,
    name: k === "platform" ? "Plattform" : names.get(k) ?? k,
    pct: pct(v.memory_mb, maxMem),
    value: v.memory_mb >= 1024 ? `${gb(v.memory_mb)} GB` : `${fmtInt(v.memory_mb)} MB`,
    containers: v.containers,
  }));
}

/** Slug → Anzeigename, für Aufmerksamkeit und Host. */
export function tenantNames(d: Dashboard): Map<string, string> {
  return new Map(d.tenants.map((t) => [t.slug, t.name || t.slug]));
}
